import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "../../utils/notify";
import PermissionGate, { ReadOnlyGate } from "../PermissionGate";
import ControlsTab from "../ControlsTab";
import FrameworkModal from "./FrameworkModal";
import FrameworkMasterManager from "./FrameworkMasterManager";
import TabNavigation from "../ui/TabNavigation";
import PageLoader from "../ui/PageLoader";
import ApiLoader from "../ui/ApiLoader";
import Pagination from "../ui/pagination";
import ComplianceOverview from "./ComplianceOverview";
import ComplianceGapChatbot from "../ui/ComplianceGapChatbot";
import UploadFileModal from "../ui/UploadFileModal";
import {
  getDashboardStatsApi,
  getFrameworkTypesApi,
  getTrustCenterFrameworksRoleBasedApi,
  getTrustCenterFrameworksCustomerAdminApi,
  getTrustCenterFrameworksEmployeeApi,
  createFrameworkApi,
  getControlsApi,
  getEmployeeFrameworkControlsApi,
  createControlApi,
  updateControlApi,
  exportDataApi,
  deleteFrameworkApi,
  toggleFrameworkStatusApi,
  fetchCustomerRequirements,
  fetchTrustCenterEnabledFrameworksApi,
  createCustomerControlResponse,
  updateCustomerControlResponse,
  getAssignedControls,
  downloadControlFileApi
} from "../../services/axios/AuthApis/TrustCenterApis";

import {
  getCompanyUsers
} from "../../services/axios/AuthApis/TeamCollabApis";

import {
  ShieldCheckIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  EyeIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  LinkIcon,
  TagIcon,
  CalendarIcon,
  UserIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  Squares2X2Icon,
  ListBulletIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/outline";
import { fetchCustomerRequirementsApi } from "../../services/axios/AuthApis/crmApis";
import { updateDocumentAcknowledgementApi } from "../../services/axios/AuthApis/DocumentLibraryApis";
import { useSelector } from "react-redux";
import { Document as PdfDocument, Page as PdfPage, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { renderAsync } from 'docx-preview';

const TRUST_CENTER_TABS = ["dashboard", "frameworks", "controls", "framework-master"];

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

/* ── helpers ── */
const _ext = (url = "") => {
  try { return new URL(url).pathname.split('.').pop().toLowerCase(); }
  catch { return url.split('?')[0].split('.').pop().toLowerCase(); }
};
const _isPdf    = (url) => _ext(url) === 'pdf';
const _isDocLike = (url) => ['docx','doc'].includes(_ext(url));
const _isOffice  = (url) => ['pptx','ppt','xlsx','xls'].includes(_ext(url));

/* ── PDF sub-viewer — all pages rendered vertically; scroll math on container unlocks Acknowledge ── */
const PdfViewer = ({ url, onLastPage }) => {
  const [numPages, setNumPages] = React.useState(null);
  const containerRef = React.useRef(null);
  const unlockedRef = React.useRef(false);
  const onLastPageRef = React.useRef(onLastPage);
  React.useEffect(() => { onLastPageRef.current = onLastPage; }, [onLastPage]);

  const onLoad = ({ numPages: n }) => { setNumPages(n); };

  const checkEnd = React.useCallback(() => {
    if (unlockedRef.current) return;
    const el = containerRef.current;
    if (!el) return;
    // scrollTop + clientHeight within 50px of total scrollHeight = bottom reached
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      unlockedRef.current = true;
      onLastPageRef.current();
    }
  }, []);

  // Attach native scroll listener directly to the DOM node (more reliable than React onScroll)
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkEnd, { passive: true });
    return () => el.removeEventListener('scroll', checkEnd);
  }, [checkEnd]);

  // After pages render, check if doc fits without scrolling (no scroll needed → unlock)
  React.useEffect(() => {
    if (!numPages) return;
    // Delay to let react-pdf finish painting page heights
    const t = setTimeout(() => {
      const el = containerRef.current;
      if (!el || unlockedRef.current) return;
      if (el.scrollHeight <= el.clientHeight + 50) {
        unlockedRef.current = true;
        onLastPageRef.current();
      }
    }, 500);
    return () => clearTimeout(t);
  }, [numPages, checkEnd]);

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto flex flex-col items-center bg-gray-800 py-6 px-2 gap-2"
    >
      <PdfDocument
        file={url}
        onLoadSuccess={onLoad}
        loading={<p className="text-gray-300 mt-16 text-sm">Loading PDF…</p>}
        error={<p className="text-red-400 mt-16 text-sm">Could not load PDF.</p>}
      >
        {numPages && Array.from({ length: numPages }, (_, i) => (
          <div key={i + 1} className="mb-3 shadow-lg">
            <PdfPage
              pageNumber={i + 1}
              width={Math.min(window.innerWidth - 64, 860)}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          </div>
        ))}
      </PdfDocument>
      {/* Bottom sentinel — visible only when all pages are above */}
      <div style={{ height: 2, width: '100%', flexShrink: 0 }} />
    </div>
  );
};

/* ── DOCX viewer — docx-preview renders faithfully (images, tables, styles preserved) ── */
const DocxViewer = ({ url, onScrollEnd }) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rendered, setRendered] = React.useState(false);
  const containerRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const unlockedRef = React.useRef(false);
  const cbRef = React.useRef(onScrollEnd);
  React.useEffect(() => { cbRef.current = onScrollEnd; }, [onScrollEnd]);

  // Fetch .docx and render into contentRef using docx-preview
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setRendered(false);
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch document');
        const blob = await response.blob();
        if (cancelled || !contentRef.current) return;
        // Clear any previous content
        contentRef.current.innerHTML = '';
        await renderAsync(blob, contentRef.current, null, {
          className: 'docx-preview-body',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          experimental: false,
        });
        if (!cancelled) {
          setRendered(true);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('DocxViewer error:', err);
          setError(err.message || 'Failed to load document');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  // Core scroll-end check
  const check = React.useCallback(() => {
    if (unlockedRef.current) return true;
    const el = containerRef.current;
    if (!el) return false;
    const { scrollTop, clientHeight, scrollHeight } = el;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 80;
    const noScrollNeeded = scrollHeight <= clientHeight + 30;
    if (atBottom || noScrollNeeded) {
      unlockedRef.current = true;
      cbRef.current();
      return true;
    }
    return false;
  }, []);

  // After content renders: scroll listener + polling
  React.useEffect(() => {
    if (!rendered) return;
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('scroll', check, { passive: true });
    const interval = setInterval(() => { if (check()) clearInterval(interval); }, 500);
    const cleanup = setTimeout(() => clearInterval(interval), 15000);
    return () => {
      el.removeEventListener('scroll', check);
      clearInterval(interval);
      clearTimeout(cleanup);
    };
  }, [rendered, check]);

  return (
    <div ref={containerRef} className="h-full overflow-y-auto bg-white">
      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-3" style={{ borderColor: "var(--app-primary-color, #26256b)" }} />
            <p className="text-gray-500 text-sm">Rendering document…</p>
          </div>
        </div>
      )}
      {/* Error message */}
      {error && (
        <div className="flex items-center justify-center py-20">
          <p className="text-red-500 text-sm">Error: {error}</p>
        </div>
      )}
      {/* docx-preview renders into this div */}
      <div
        ref={contentRef}
        className="mx-auto"
        style={{ maxWidth: 920, display: loading ? 'none' : 'block' }}
      />
      {/* Bottom spacer */}
      {!loading && <div style={{ height: 20, width: '100%', flexShrink: 0 }} />}
    </div>
  );
};

/* ── Office embed viewer (pptx/xlsx only) — scroll past iframe to unlock ── */
const EmbedViewer = ({ url, onConfirm }) => {
  const containerRef = React.useRef(null);
  const unlockedRef  = React.useRef(false);
  const onConfirmRef = React.useRef(onConfirm);
  React.useEffect(() => { onConfirmRef.current = onConfirm; }, [onConfirm]);

  const src = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

  const checkEnd = React.useCallback(() => {
    if (unlockedRef.current) return;
    const el = containerRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      unlockedRef.current = true;
      onConfirmRef.current();
    }
  }, []);

  // Attach native scroll listener to the container
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkEnd, { passive: true });
    // Also check on mount in case content is short
    const t = setTimeout(checkEnd, 500);
    return () => { el.removeEventListener('scroll', checkEnd); clearTimeout(t); };
  }, [checkEnd]);

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto bg-gray-800 flex flex-col"
    >
      {/* iframe — intentionally taller than container so outer div must scroll */}
      <iframe
        src={src}
        title="Document Viewer"
        className="w-full border-0 block"
        style={{ height: '88vh', minHeight: 600, flexShrink: 0 }}
        allowFullScreen
      />
      {/* End-of-doc strip — visible only after scrolling past iframe */}
      <div className="shrink-0 bg-gray-900 px-5 py-5 border-t border-gray-700 flex items-center gap-3 text-gray-400 text-sm">
        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: "var(--app-primary-color, #26256b)" }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        You have reached the end of the document. Click <strong className="text-white mx-1">Acknowledge</strong> below to confirm.
      </div>
    </div>
  );
};

/* ── Main modal ── */
const DocViewerModal = ({ item, employeeId, onClose }) => {
  const [ackEnabled, setAckEnabled] = React.useState(false);
  const [acknowledging, setAcknowledging] = React.useState(false);
  const [acknowledged, setAcknowledged] = React.useState(false);
  const ackEnabledRef = React.useRef(false);

  // Stable callback — never changes identity, never allows ackEnabled to go back to false
  const enableAck = React.useCallback(() => {
    if (ackEnabledRef.current) return;
    ackEnabledRef.current = true;
    setAckEnabled(true);
  }, []);

  const fileUrl  = item.url || '';
  const isPdf    = _isPdf(fileUrl);
  const isDoc    = _isDocLike(fileUrl);
  const isOffice = _isOffice(fileUrl);
  // Determine viewer type: pdf | docx | office | fallback
  const viewerType = isPdf ? 'pdf' : isDoc ? 'docx' : isOffice ? 'office' : 'office';

  const handleAcknowledge = async () => {
    if (!employeeId) {
      toast.error("No employee linked to this control — cannot record acknowledgement.");
      return;
    }
    setAcknowledging(true);
    try {
      await updateDocumentAcknowledgementApi({
        employee_id: employeeId,
        sub_category_id: item.sub_category_id,
        control_id: item.document_id,
        is_acknowledge: 1,
      });
      setAcknowledged(true);
      toast.success("Document acknowledged successfully!");
    } catch {
      toast.error("Failed to record acknowledgement.");
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col bg-black/80">
      {/* Header */}
      <div className="flex items-center justify-between bg-gray-900 px-5 py-3 shrink-0 border-b border-gray-700">
        <span className="text-white font-semibold text-sm truncate">
          {item.category ? `Document — ${item.category}` : 'Document Viewer'}
        </span>
        <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none ml-4 shrink-0">✕</button>
      </div>

      {/* Viewer — fills remaining space; each viewer handles its own scroll */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {viewerType === 'pdf' && (
          <PdfViewer url={fileUrl} onLastPage={enableAck} />
        )}
        {viewerType === 'docx' && (
          <DocxViewer url={fileUrl} onScrollEnd={enableAck} />
        )}
        {(viewerType === 'office' || viewerType === 'fallback') && (
          <EmbedViewer url={fileUrl} onConfirm={enableAck} />
        )}
      </div>

      {/* Acknowledge bar — sticks to the bottom */}
      <div className="shrink-0 bg-gray-900 px-5 py-3 flex items-center justify-end gap-4 border-t border-gray-700">
        {acknowledged ? (
          <>
            <span className="flex items-center gap-2 text-green-400 font-semibold text-sm mr-auto">
              <CheckCircleIcon className="w-5 h-5" /> Acknowledged
            </span>
            <button onClick={onClose}
              className="px-4 py-2 border border-gray-600 rounded-lg text-sm text-gray-300 hover:bg-gray-800">
              Close
            </button>
          </>
        ) : (
          <button
            disabled={!ackEnabled || acknowledging}
            onClick={handleAcknowledge}
            className={`shrink-0 px-6 py-2 rounded-lg text-sm font-semibold transition-all ${
              ackEnabled && !acknowledging
                ? 'text-white shadow-lg'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
            style={ackEnabled && !acknowledging ? { backgroundColor: "var(--app-primary-color, #26256b)" } : {}}
          >
            {acknowledging ? 'Saving…' : 'Acknowledge'}
          </button>
        )}
      </div>
    </div>
  );
};


const ComplianceDashboard = ({
  frameworks: propFrameworks = [],
  enabledFrameworks: propEnabledFrameworks = [],
}) => {
  const formatEvidenceLinks = (links = []) => {
    if (!Array.isArray(links) || links.length === 0) return "";
    return links.map((url, index) => `${index + 1}. ${url}`).join("\n");
  };

  const parseEvidenceLinks = (value = "") => {
    return String(value)
      .split("\n")
      .map((line) => line.replace(/^\s*\d+\.\s*/, "").trim())
      .filter(Boolean);
  };

  const getEvidenceLinkDetails = (
    control,
    fallbackValue = "",
    selectedCategory = ""
  ) => {
    const details = Array.isArray(control?.evidence_link_details)
      ? control.evidence_link_details
          .map((item) => ({
            url: typeof item === "string" ? item : item?.url,
            category:
              typeof item === "string"
                ? ""
                : String(item?.category || "").trim().toLowerCase(),
          }))
          .filter((item) => item.url)
      : [];

    const normalizedSelectedCategory = String(selectedCategory || "")
      .trim()
      .toLowerCase();

    if (details.length > 0) {
      if (!normalizedSelectedCategory) return details;
      return details.filter(
        (item) => item.category === normalizedSelectedCategory
      );
    }

    const fallbackDetails = parseEvidenceLinks(fallbackValue).map((url) => ({
      url,
      category: "",
    }));

    if (!normalizedSelectedCategory) return fallbackDetails;

    return fallbackDetails.filter(
      (item) => item.category === normalizedSelectedCategory
    );
  };

  const { user, token } = useAuth();
  const selectedCompany = useSelector((state) => state.company.selectedCompany);
  const customerId = user?.id;
  const isCustomerAdmin = user?.role === "customer_admin";
  const isSuperAdmin = user?.role === "admin" || user?.role === "platform_admin";
  const isFrameworkManagerRole =
    isSuperAdmin ||
    user?.role === "customer_admin" ||
    user?.role === "security_analyst" ||
    user?.role === "auditor";
  const editAccess = user?.role === "auditor"
  // Ref for debouncing search
  const searchDebounceTimer = useRef(null);
  const searchInputRef = useRef(null);

  // State management
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("interactive"); // enhanced or interactive
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    TRUST_CENTER_TABS.includes(initialTab) ? initialTab : "dashboard",
  );

  // Lets a notification deep-link into a specific tab via ?tab=controls,
  // same convention CSPM already uses.
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (TRUST_CENTER_TABS.includes(tab)) setActiveTab(tab);
  }, [searchParams]);


  const [dashboardStats, setDashboardStats] = useState(null);
  const [frameworks, setFrameworks] = useState(propFrameworks);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [controls, setControls] = useState([]);
  const [selectedControl, setSelectedControl] = useState(null);
  const [enabledFrameworks, setEnabledFrameworks] = useState(
    propEnabledFrameworks
  );
  const [frameworkTypes, setFrameworkTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [assignedControls, setAssignedControls] = useState([]);
  const [assignedControlsLoading, setAssignedControlsLoading] = useState(false);
  const [frameworkLoading, setFrameworkLoading] = useState(true);
  const userDetail = useSelector((state) => state.user);
  let customerMasterId = null;
  if (userDetail?.role === 'customer_admin') {
      customerMasterId = userDetail?.user?.customer_master_id;
  } else if (userDetail?.user?.role == 'security_analyst') { 
      const selectedCompany = useSelector((state) => state.company.selectedCompany);
      customerMasterId = selectedCompany?.company_id;
  }

  // Pagination states for controls
  const [controlsPagination, setControlsPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  });
  const [controlsSearchTerm, setControlsSearchTerm] = useState("");
  const [controlsLoading, setControlsLoading] = useState(false);
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [selectedControlForAssign, setSelectedControlForAssign] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [controlsFilters, setControlsFilters] = useState({
    search: "",
    control_id: "",
    title: "",
    assigned_to: "",
    date: ""
  });
  const [frameworkPagination, setFrameworkPagination] = useState({
    page: 1,
    page_size: 20,
    total_pages: 1,
    count: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    type: "",
    category: "",
    country: "",
    status: "",
  });

  const [searchTerm, setSearchTerm] = useState("");

  // Modal states
  const [showFrameworkModal, setShowFrameworkModal] = useState(false);
  const [showControlModal, setShowControlModal] = useState(false);
  const [showControlDetailModal, setShowControlDetailModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [docViewerItem, setDocViewerItem] = useState(null);
  const [openMenuFrameworkId, setOpenMenuFrameworkId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "Confirm",
    confirmButtonClass: "hover:opacity-90",
    confirmButtonStyle: { backgroundColor: "var(--app-primary-color, #26256b)" },
  });
  const [assignForm, setAssignForm] = useState({
    employee_id: "",
    employee_name: "",
    due_date: "",
  });
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeDropdown, setEmployeeDropdown] = useState([]);
  // Form data
  const [frameworkFormData, setFrameworkFormData] = useState({
    name: "",
    type: "",
    category: "",
    country: "",
    region: "",
    industry: "",
    description: "",
    version: "",
  });

  const [controlFormData, setControlFormData] = useState({
    control_id: "",
    title: "",
    description: "",
    requirement_type: "mandatory",
    assigned_to: "",
    due_date: "",
  });
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [uploadModalFile, setUploadModalFile] = useState(null);
  const [evidenceRemarks, setEvidenceRemarks] = useState("");

  const [controlUpdateData, setControlUpdateData] = useState({
    status: "No",
    evidence_type: "",
    evidence_category: "",
    evidence_description: "",
    evidence_link: "",
    submission_status: "Draft",
  });

  const hasAssignmentFilters =
    !!controlsFilters.assigned_to ||
    !!controlsFilters.date;

  // Framework regions and industries for filters (static for now)
  const regions = [
    "Global",
    "North America",
    "Europe",
    "Asia Pacific",
    "South America",
    "Africa",
  ];
  const industries = [
    "Healthcare",
    "Financial Services",
    "Technology",
    "Retail",
    "Manufacturing",
    "Government",
    "Education",
  ];

  useEffect(() => {
    loadDashboardData();
    if (isFrameworkManagerRole) return;
    loadFrameworkTypes();
  }, [isFrameworkManagerRole, user?.id, selectedCompany]);

  useEffect(() => {
    if (isFrameworkManagerRole) return;
    loadFrameworks();
  }, [frameworkPagination.page, frameworkPagination.page_size, filters, isFrameworkManagerRole]);

  // For framework manager roles, briefly show loader then mark ready
  useEffect(() => {
    if (!isFrameworkManagerRole) return;
    const init = async () => {
      try {
        await loadFrameworkTypes();
      } catch (e) {
        console.error("Failed initial load:", e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isFrameworkManagerRole]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);


  useEffect(() => {
    if (selectedControl) {
      // Reset form states when a new control is opened
      setControlUpdateData({
        status: selectedControl.status || "No",
        submission_status: "Draft",
        evidence_type: "default",
        evidence_category: "",
        evidence_description: "",
        evidence_link:
          formatEvidenceLinks(selectedControl.evidence_links) ||
          selectedControl.evidence_link ||
          "",
      });

      setEvidenceFile(null);
      setEvidenceRemarks("");
    }
  }, [selectedControl]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openMenuFrameworkId &&
        !event.target.closest(".framework-menu-container")
      ) {
        setOpenMenuFrameworkId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuFrameworkId]);

  // Helper function to extract ID from framework object
  const getFrameworkId = (framework) => {
    if (!framework) return null;
    return framework.id || framework._id || framework._id?.$oid || null;
  };




  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("enabledModules")) || [];
      const merged = [...new Set([...propEnabledFrameworks, ...stored])];

      setEnabledFrameworks(merged);
    } catch {
      setEnabledFrameworks(propEnabledFrameworks);
    }
  }, [propEnabledFrameworks]);

  useEffect(() => {
    if (user?.role !== "end_user") return;

    if (!Array.isArray(frameworks) || frameworks.length === 0) {
      setSelectedFramework(null);
      return;
    }

    if (!selectedFramework) {
      setSelectedFramework(frameworks[0]);
    }
  }, [user?.role, frameworks, selectedFramework]);


  useEffect(() => {
    // Do not search for tiny inputs
    const term = (employeeSearch || "").trim();
    if (term.length < 2) {
      // hide dropdown for empty or 1-char input
      setEmployeeDropdown([]);
      console.debug("[employeeSearch] too short — skipping API", term);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        console.debug("[employeeSearch] will call API with:", term);
        const data = await getCompanyUsers(customerMasterId, 1, 50, term);
        console.debug("[employeeSearch] API returned count:", data?.results?.length);
        setEmployeeDropdown(data.results || []);
      } catch (err) {
        console.error("Error fetching company users:", err);
        setEmployeeDropdown([]);
      }
    }, 250); // debounce

    return () => clearTimeout(timer);
  }, [employeeSearch, customerMasterId]);





  const loadDashboardData = async () => {
    try {
      const effectiveCustomerId =
        user?.role === "customer_admin"
          ? user?.id
          : (user?.role === "security_analyst" || user?.role === "auditor")
            ? selectedCompany?.user_id
            : null;
      const stats = await getDashboardStatsApi(effectiveCustomerId);
      setDashboardStats(stats);
    } catch (error) {
      console.error("Failed to load dashboard stats:", error);
    }
  };


  const loadFrameworkTypes = async () => {
    try {
      const data = await getFrameworkTypesApi();
      console.log("📦 Framework types loaded:", data);
      setFrameworkTypes(data?.types || []);
      setCategories(data?.categories || []); // Now an array of {id, name} objects
      setCountries(data?.countries || []);
      console.log(
        "✅ Categories set:",
        data?.categories?.length || 0,
        "categories"
      );
    } catch (error) {
      console.error("Failed to load framework types:", error);
      // Fallback to default types if API fails
      setFrameworkTypes([]);
      setCategories([]);
      setCountries([]);
    }
  };

  const handleAssignSubmit = async () => {
    try {
      const payload = {
        employee_id: assignForm.employee_id,
        control_id: selectedControlForAssign.mongoId,
        due_date: assignForm.due_date,
        compliance_status: "pending",
        submission_status: "not_submitted",
        evidence_description: "",
        evidence_link: "",
        file: null,
        remarks: "",
        default: 0,
      };

      await createCustomerControlResponse(payload);

      toast.success("Assigned successfully!", {
        position: "top-right",
        autoClose: 2000
      });

      setShowAssignModal(false);
      setAssignForm({ employee_id: "", employee_name: "", due_date: "" });

      // ✅ FIX: load controls using the correct frameworkId
      const frameworkId = getFrameworkId(selectedFramework);
      if (frameworkId) {
        await loadControls(frameworkId);
      }

      // then refresh assigned_to
      await refreshAssignedTo();

    } catch (error) {
      console.error("Error saving assignment:", error);
      toast.error("Failed to assign");
    }
  };


  const buildQuery = () => {
    const params = new URLSearchParams(filters);

    Object.keys(filters).forEach(
      k => !filters[k] && params.delete(k)
    );

    return params.toString();
  };



  const loadFrameworks = async () => {
    setFrameworkLoading(true);

    try {
      const query = buildQuery();
      let res;

      if (user?.role === "customer_admin") {
        res = await getTrustCenterFrameworksCustomerAdminApi(
          frameworkPagination.page,
          frameworkPagination.page_size,
          user.id,
          query
        );
      } else if (user?.role === "security_analyst" || user?.role === "auditor") {
        if (selectedCompany) {
          res = await getTrustCenterFrameworksCustomerAdminApi(
            frameworkPagination.page,
            frameworkPagination.page_size,
            selectedCompany.user_id,
            query
          );
        }
      }
      else if (user?.role === "end_user") {
        res = await getTrustCenterFrameworksEmployeeApi(user.id);
      }
      else {
        res = await getTrustCenterFrameworksRoleBasedApi(
          frameworkPagination.page,
          frameworkPagination.page_size,
          query
        );
      }

      // 🔥 IMPORTANT: defensive fallback
      const results = res?.results ?? [];
      setFrameworks(results);
      console.log({ results })
      setFrameworkPagination(p => ({
        ...p,
        total_pages: res?.total_pages ?? 1,
        count: res?.count ?? results.length
      }));

    } catch (err) {
      console.error("Failed to load frameworks", err);
      setFrameworks([]);
    } finally {
      // 🔥 THIS MUST ALWAYS RUN
      setFrameworkLoading(false);
      setLoading(false);
    }
  };



  // useEffect(() => {

  // }, [isCustomerAdmin,customerId]);
  const loadControls = async (
    frameworkId,
    page = 1,
    pageSize = 20,
    filters = {}
  ) => {
    try {
      setControlsLoading(true);
      const data = user.role === "end_user"
        ? await getEmployeeFrameworkControlsApi(
            frameworkId,
            page,
            pageSize,
            filters
          )
        : await getControlsApi(
            frameworkId,
            page,
            pageSize,
            filters
          );
      console.log({data})
      setControls(data.results || []);
      setControlsPagination({
        currentPage: data.current_page,
        pageSize: pageSize === "all" ? "all" : data.page_size,
        totalCount: data.count,
        totalPages: data.total_pages,
      });
    } finally {
      setControlsLoading(false);
    }
  };


  useEffect(() => {
    if (!selectedFramework) return;

    const frameworkId = getFrameworkId(selectedFramework);
    if (!frameworkId) return;

    const load = async () => {
      await loadControls(
        frameworkId,
        1, // reset page on filter change
        controlsPagination.pageSize,
        controlsFilters
      );

      await refreshAssignedTo();
    };

    load();
  }, [selectedFramework, controlsFilters]);



  const handleControlsPageChange = (newPage, newPageSize) => {
    const frameworkId = getFrameworkId(selectedFramework);
    if (!frameworkId) return;

    const pageSize = newPageSize !== undefined ? newPageSize : controlsPagination.pageSize;
    setControlsLoading(true);
    loadControls(
      frameworkId,
      newPage,
      pageSize,
      controlsFilters
    );
  };


  const handleControlsSearch = () => {
    const frameworkId = getFrameworkId(selectedFramework);
    if (!frameworkId) return;

    const searchValue = searchInputRef.current?.value || "";

    setControlsSearchTerm(searchValue);
    setControlsLoading(true);

    loadControls(
      frameworkId,
      1,
      controlsPagination.pageSize,
      {
        ...controlsFilters,
        search: searchValue
      }
    );
  };



  const handleCreateFramework = async () => {
    try {
      // Validate required fields
      if (!frameworkFormData.name || !frameworkFormData.name.trim()) {
        toast.error("Framework name is required");
        return;
      }
      if (!frameworkFormData.type) {
        toast.error("Framework type is required");
        return;
      }
      if (!frameworkFormData.category) {
        toast.error("Category is required");
        return;
      }
      if (!frameworkFormData.country) {
        toast.error("Country is required");
        return;
      }
      if (
        !frameworkFormData.description ||
        !frameworkFormData.description.trim()
      ) {
        toast.error("Description is required");
        return;
      }

      await createFrameworkApi(frameworkFormData);
      toast.success("Framework created successfully");
      setShowFrameworkModal(false);
      resetFrameworkForm();
      loadFrameworks();
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to create framework");
    }
  };

  const handleCreateControl = async () => {
    try {
      const frameworkId = getFrameworkId(selectedFramework);
      const controlData = {
        ...controlFormData,
        framework_id: frameworkId,
      };
      await createControlApi(controlData);
      toast.success("Control created successfully");
      setShowControlModal(false);
      resetControlForm();
      if (frameworkId) {
        loadControls(frameworkId);
      }
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to create control");
    }
  };

  const handleUpdateControl = async () => {
    try {
      console.log("🚀 selectedControl BEFORE UPDATE:", selectedControl);
      if (!selectedControl?.response_id) {
        toast.error("Response ID not found!");
        return;
      }

      if (controlUpdateData.evidence_type === "customize" && !evidenceFile) {
        toast.error("Please upload a file");
        return;
      }

      const formData = new FormData();
      formData.append("compliance_status", controlUpdateData.status);
      formData.append("submission_status", controlUpdateData.submission_status);
      formData.append("evidence_type", controlUpdateData.evidence_type || "default");
      formData.append("evidence_description", controlUpdateData.evidence_description);
      formData.append(
        "evidence_link",
        controlUpdateData.evidence_type === "customize"
          ? ""
          : controlUpdateData.evidence_link || ""
      );
      formData.append(
        "category",
        controlUpdateData.evidence_category || ""
      );
      formData.append(
        "default",
        controlUpdateData.evidence_type === "customize" ? "1" : "0"
      );

      if (evidenceFile) formData.append("file", evidenceFile);
      if (evidenceRemarks) formData.append("remarks", evidenceRemarks);

      await updateCustomerControlResponse(selectedControl.response_id, formData);

      toast.success("Control updated!", { autoClose: 2000 });

      setShowControlDetailModal(false);

      const frameworkId = getFrameworkId(selectedFramework);
      await loadControls(frameworkId);
      await refreshAssignedTo();

    } catch (err) {
      console.error("Failed to update control:", err);
      toast.error("Update failed");
    }
  };


  const handleExport = async (format = "csv") => {
    try {
      toast.loading("Generating export...", { id: "export" });
      const blob = await exportDataApi(format, token);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trust_center_export_${new Date().toISOString().split("T")[0]
        }.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded", { id: "export" });
    } catch (error) {
      toast.error("Export failed", { id: "export" });
    }
  };

  const handleDeleteFramework = async (frameworkId, frameworkName) => {
    // Show confirmation modal instead of alert
    setConfirmModalData({
      title: "Delete Framework",
      message: `Are you sure you want to delete "${frameworkName}"? This action cannot be undone.`,
      confirmText: "Delete",
      confirmButtonClass: "bg-red-600 hover:bg-red-700",
      onConfirm: async () => {
        try {
          await deleteFrameworkApi(frameworkId);
          toast.success("Framework deleted successfully");
          loadFrameworks();
          loadDashboardData();
          setShowConfirmModal(false);
        } catch (error) {
          toast.error("Failed to delete framework");
          setShowConfirmModal(false);
        }
      },
    });
    setShowConfirmModal(true);
  };


  const fetchCompanyEmployees = async () => {
    try {
      if (!customerMasterId) {
        console.error("Missing customerMasterId");
        return;
      }

      const data = await getCompanyUsers(customerMasterId, 1, 500);
      setCompanyEmployees(data.results || []);
      console.log("Employees loaded:", data.results);

    } catch (err) {
      console.error("❌ Error fetching employees:", err);
    }
  };

  useEffect(() => {
    // Only for customer_admin
    if (user.role !== "customer_admin") return;

    // Only when assignment-related filters are used
    if (!hasAssignmentFilters) return;

    setAssignedControlsLoading(true);

    // Simulate “processing / syncing”
    const timer = setTimeout(() => {
      setAssignedControlsLoading(false);
    }, 300); // ⏱️ small delay for UX (200–400ms is ideal)

    return () => clearTimeout(timer);
  }, [
    controlsFilters.assigned_to,
    controlsFilters.date
  ]);

  const refreshAssignedTo = async () => {
    try {
      setAssignedControlsLoading(true); // 🔥 START LOADER
      const customerId = user?.customer_master_id || user?.customer_id;
      const frameworkId = getFrameworkId(selectedFramework);
      const employeeId = user?.role === "end_user" ? user?.id : null;

      if (!frameworkId) return;

      const assigned = await getAssignedControls(customerId, frameworkId, employeeId);
      console.log("Assigned API result:", assigned);
      // SAFETY: assigned may be undefined/null
      const assignedList = assigned?.results || [];

      setControls(prev =>
        prev.map(ctrl => {
          const match = assignedList.find(a => String(a.control_id) === String(ctrl.id));

          return {
            ...ctrl,
            assigned_to: match?.assigned_to || "",
            due_date: match?.due_date || "",
            response_id: match?.response_id || null,
            status: match?.status || "Pending"
          };
        })
      );
    } catch (err) {
      console.error("Error refreshing assigned_to:", err);
    }
    finally {
      setAssignedControlsLoading(false); // 🔥 STOP LOADER
    }
  };



  const filteredControls = useMemo(() => {
    let list = [...controls];

    // 🔍 control_id
    if (controlsFilters.control_id) {
      list = list.filter(c =>
        c.control_id
          ?.toLowerCase()
          .includes(controlsFilters.control_id.toLowerCase())
      );
    }

    // 📝 title
    if (controlsFilters.title) {
      list = list.filter(c =>
        c.title
          ?.toLowerCase()
          .includes(controlsFilters.title.toLowerCase())
      );
    }

    // 👤 assigned_to (CUSTOMER ADMIN)
    if (controlsFilters.assigned_to) {
      list = list.filter(c =>
        c.assigned_to
          ?.toLowerCase()
          .includes(controlsFilters.assigned_to.toLowerCase())
      );
    }

    // 📅 due_date (CUSTOMER ADMIN)
    if (controlsFilters.date) {
      list = list.filter(c => {
        if (!c.due_date) return false;
        return String(c.due_date).startsWith(controlsFilters.date);
      });
    }

    return list;
  }, [controls, controlsFilters]);


  const handleToggleFrameworkStatus = async (
    frameworkId,
    currentStatus,
    frameworkName
  ) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    const action = newStatus === "Active" ? "enable" : "disable";

    // Show confirmation modal instead of alert
    setConfirmModalData({
      title: `${action === "enable" ? "Enable" : "Disable"} Framework`,
      message: `Are you sure you want to ${action} "${frameworkName}"?`,
      confirmText: action === "enable" ? "Enable" : "Disable",
      confirmButtonClass:
        action === "enable"
          ? "bg-green-600 hover:bg-green-700"
          : "bg-orange-600 hover:bg-orange-700",
      onConfirm: async () => {
        try {
          await toggleFrameworkStatusApi(frameworkId, newStatus);
          toast.success(`Framework ${action}d successfully`);
          loadFrameworks();
          loadDashboardData();
          setShowConfirmModal(false);
        } catch (error) {
          toast.error(`Failed to ${action} framework`);
          setShowConfirmModal(false);
        }
      },
    });
    setShowConfirmModal(true);
  };

  const resetFrameworkForm = () => {
    setFrameworkFormData({
      name: "",
      type: "",
      category: "",
      country: "",
      region: "",
      industry: "",
      description: "",
      version: "",
    });
  };

  const resetControlForm = () => {
    setControlFormData({
      control_id: "",
      title: "",
      description: "",
      requirement_type: "mandatory",
      assigned_to: "",
      due_date: "",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "text-green-700 bg-green-100 border-green-300";

      case "Pending":
        return "text-red-700 bg-red-100 border-red-300";

      default:
        return "text-gray-600 bg-gray-100 border-gray-300";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "Information Security":
        return <ShieldCheckIcon className="w-5 h-5" />;
      case "Cyber Security":
        return <ExclamationTriangleIcon className="w-5 h-5" />;
      case "Data Protection & Privacy":
        return <DocumentTextIcon className="w-5 h-5" />;
      case "Industry-specific":
        return <BuildingOfficeIcon className="w-5 h-5" />;
      default:
        return <ClipboardDocumentCheckIcon className="w-5 h-5" />;
    }
  };

  if (!Array.isArray(frameworks)) {
    console.warn("⚠️ frameworks prop is not an array:", frameworks);
    setFrameworks([]); // clear invalid data
    return null; // temporarily stop rendering
  }

  useEffect;

  const filteredFrameworks = frameworks.filter((framework) => {
    const normalize = (s) =>
      String(s || "")
        .trim()
        .toLowerCase();

    // ✅ Admins, Customer Admins, and End Users (already API-filtered) see frameworks
    if (
      (user?.role === "admin" ||
        user?.role === "platform_admin" ||
        user?.role === "customer_admin" ||
        user?.role === "end_user" ||
        ((user?.role === "security_analyst" || user?.role === "auditor") && selectedCompany))
    ) {
      return true;
    }
    if (enabledFrameworks.length === 0)
      // ✅ Others see only enabled
      return false;
    return enabledFrameworks.map(normalize).includes(normalize(framework.name));
  });

  // Enhanced Dashboard View Component
  // Interactive Dashboard View Component
  const InteractiveDashboardView = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 relative">

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 10%, transparent)" }}>
              <FunnelIcon className="w-5 h-5" style={{ color: "var(--app-primary-color, #26256b)" }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Framework Filters
              </h3>
              <p className="text-xs text-gray-500">
                {frameworkLoading ? (
                  <span style={{ color: "var(--app-primary-color, #26256b)" }}>Loading...</span>
                ) : (
                  <span>
                    Showing{" "}
                    <span className="font-semibold" style={{ color: "var(--app-primary-color, #26256b)" }}>
                      {filteredFrameworks.length}
                    </span>{" "}
                    framework{filteredFrameworks.length !== 1 ? "s" : ""}
                    {(filters.type ||
                      filters.category ||
                      filters.country ||
                      filters.region ||
                      filters.industry) &&
                      " with active filters"}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <PermissionGate permission="trust_center_edit">
              {isSuperAdmin ? (
                <button 
                  type="button"
                  onClick={() => setShowFrameworkModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white transition-all duration-200 shadow-sm hover:opacity-90 hover:shadow-md"
                  style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  New Framework
                </button>
              ) : ""}

            </PermissionGate>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Certification Type Filter */}
          <div className="space-y-2">
            <label className="flex items-center text-xs font-medium uppercase tracking-wider">
              <ShieldCheckIcon className="w-4 h-4 mr-1.5" style={{ color: "var(--app-primary-color, #26256b)" }} />
              Type{" "}
              {frameworkTypes.length > 0 && (
                <span className="ml-1 text-gray-400">
                  ({frameworkTypes.length})
                </span>
              )}
            </label>
            <select
              value={filters.type}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, type: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
            >
              <option value="">All Types</option>
              {frameworkTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div className="space-y-2">
            <label className="flex items-center text-xs font-medium uppercase tracking-wider">
              <TagIcon className="w-4 h-4 mr-1.5" style={{ color: "var(--app-primary-color, #26256b)" }} />
              Category{" "}
              {categories.length > 0 && (
                <span className="ml-1 text-gray-400">
                  ({categories.length})
                </span>
              )}
            </label>
            <select
              value={filters.category}
              onChange={(e) => {
                console.log("📂 Category filter changed to:", e.target.value);
                const selectedCategory = categories.find(
                  (c) => c.id === e.target.value
                );
                console.log("📂 Selected category object:", selectedCategory);
                setFilters((prev) => ({ ...prev, category: e.target.value }));
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Country Filter */}
          <div className="space-y-2">
            <label className="flex items-center text-xs font-medium uppercase tracking-wider">
              <svg
                className="w-4 h-4 mr-1.5"
                style={{ color: "var(--app-primary-color, #26256b)" }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                />
              </svg>
              Country{" "}
              {countries.length > 0 && (
                <span className="ml-1 text-gray-400">({countries.length})</span>
              )}
            </label>
            <select
              value={filters.country}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, country: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
            >
              <option value="">All Countries</option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="space-y-2">
            <label className="flex items-center text-xs font-medium uppercase tracking-wider">
              <CheckCircleIcon className="w-4 h-4 mr-1.5" style={{ color: "var(--app-primary-color, #26256b)" }} />
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Active Filters Display */}
        {(filters.type ||
          filters.category ||
          filters.country ||
          filters.status ||
          filters.region ||
          filters.industry) && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-xs font-medium text-gray-600">
                  Active Filters:
                </span>
                {filters.type && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 12%, white)", color: "var(--app-primary-color, #26256b)" }}
                  >
                    Type: {filters.type}
                    <button
                      type="button"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, type: "" }))
                      }
                      className="ml-1.5"
                    >
                      <XCircleIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {filters.category && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 12%, white)", color: "var(--app-primary-color, #26256b)" }}
                  >
                    Category:{" "}
                    {categories.find((c) => c.id === filters.category)?.name ||
                      filters.category}
                    <button
                      type="button"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, category: "" }))
                      }
                      className="ml-1.5"
                    >
                      <XCircleIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {filters.country && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 12%, white)", color: "var(--app-primary-color, #26256b)" }}
                  >
                    Country: {filters.country}
                    <button
                      type="button"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, country: "" }))
                      }
                      className="ml-1.5"
                    >
                      <XCircleIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {filters.status && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 12%, white)", color: "var(--app-primary-color, #26256b)" }}
                  >
                    Status: {filters.status}
                    <button
                      type="button"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, status: "" }))
                      }
                      className="ml-1.5"
                    >
                      <XCircleIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {filters.region && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 12%, white)", color: "var(--app-primary-color, #26256b)" }}
                  >
                    Region: {filters.region}
                    <button
                      type="button"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, region: "" }))
                      }
                      className="ml-1.5"
                    >
                      <XCircleIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {filters.industry && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 12%, white)", color: "var(--app-primary-color, #26256b)" }}
                  >
                    Industry: {filters.industry}
                    <button
                      type="button"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, industry: "" }))
                      }
                      className="ml-1.5"
                    >
                      <XCircleIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
      </div>

      {/* Frameworks Grid */}
      {loading ? (
        <ApiLoader label="Loading frameworks..." className="py-20" />
      ) : filteredFrameworks.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="text-gray-400 mb-4">
              <svg
                className="h-16 w-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-gray-600 text-lg font-medium">
              No frameworks found
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Try adjusting your filters
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFrameworks.map((framework) => {
            const frameworkId = getFrameworkId(framework);
            const isMenuOpen = openMenuFrameworkId === frameworkId;
            const isInactive = framework.status === "Inactive";

            return (

              <motion.div
                key={frameworkId || framework.name}
                whileHover={{ y: -3 }}
                className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition cursor-pointer relative group ${isInactive
                  ? "bg-gray-100 border-gray-300 opacity-75"
                  : "border-gray-200"
                  }`}
                onClick={() => {
                  console.log("Framework clicked:", framework);
                  console.log("Framework ID:", frameworkId);

                  // Reset controls and show loading state
                  setControls([]);
                  setControlsLoading(true);
                  setControlsSearchTerm("");
                  if (searchInputRef.current) {
                    searchInputRef.current.value = "";
                  }

                  // Reset pagination
                  setControlsPagination({
                    currentPage: 1,
                    pageSize: 20,
                    totalCount: 0,
                    totalPages: 0,
                  });

                  // Set selected framework and switch to controls tab
                  setSelectedFramework(framework);
                  setActiveTab("controls");
                }}
              >
                {/* Three-dot menu button */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity z-10 framework-menu-container">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuFrameworkId(isMenuOpen ? null : frameworkId);
                    }}
                    className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <EllipsisVerticalIcon className="w-5 h-5 text-gray-600" />
                  </button>

                  {/* Dropdown menu */}
                  {isMenuOpen && (user?.role !="end_user") && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFrameworkStatus(
                            frameworkId,
                            framework.status,
                            framework.name
                          );
                          setOpenMenuFrameworkId(null);
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <NoSymbolIcon className="w-4 h-4 mr-3 text-orange-600" />
                        {framework.status === "Active" ? "Disable" : "Enable"}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFramework(frameworkId, framework.name);
                          setOpenMenuFrameworkId(null);
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <TrashIcon className="w-4 h-4 mr-3" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                <div className="p-5 flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${isInactive ? "bg-gray-200" : ""}`}
                      style={!isInactive ? { backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 12%, white)" } : {}}
                    >
                      {getTypeIcon(framework.type)}
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <h3
                          className={`text-base font-semibold ${isInactive ? "text-gray-500" : "text-gray-900"
                            }`}
                        >
                          {framework.name}
                        </h3>

                        {isInactive && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                            Inactive
                          </span>
                        )}
                      </div>

                      <p
                        className={`text-sm ${isInactive ? "text-gray-400" : "text-gray-500"
                          }`}
                      >
                        {framework.type}
                      </p>
                    </div>
                  </div>

                </div>

                {/* Description */}
                <div className="px-5 pb-3">
                  <p
                    className={`text-sm line-clamp-2 ${isInactive ? "text-gray-400" : "text-gray-600"
                      }`}
                  >
                    {framework.description}
                  </p>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t text-xs text-gray-500 flex justify-between">
                  <span>v{framework.version || "1.0"}</span>
                  {/* optional right-side meta later */}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      {frameworkPagination.total_pages > 1 && (
        <Pagination
          currentPage={frameworkPagination.page}
          totalPages={frameworkPagination.total_pages}
          totalCount={frameworkPagination.count}
          pageSize={frameworkPagination.page_size}
          onPageChange={(page, size) =>
            setFrameworkPagination((p) => ({ ...p, page, page_size: size }))
          }
          className="mt-6"
        />
      )}

    </div>
  );

  // Main render
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <PageLoader title="Loading Trust Center…" minH="min-h-screen" />
        </div>
      </div>
    );
  }

  const managerCustomerId =
    user?.role === "security_analyst" || user?.role === "auditor"
      ? selectedCompany?.user_id
      : user?.role === "customer_admin"
        ? user?.id
        : null;




  return (

    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto bg-white min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Trust Center
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Comprehensive compliance management with frameworks, controls, and
          evidence repository
        </p>
      </div>

      {/* Navigation Tabs */}
      <TabNavigation
        tabs={isFrameworkManagerRole || user?.role == "end_user"
          ? [
              { id: "dashboard", name: "Dashboard", icon: ChartBarIcon },
              { id: "framework-master", name: "Framework", icon: ShieldCheckIcon },
            ]
          : [
              { id: "dashboard", name: "Dashboard", icon: ChartBarIcon },
              { id: "frameworks", name: "Frameworks", icon: ShieldCheckIcon },
              { id: "controls", name: "Controls", icon: ClipboardDocumentCheckIcon, disabled: !selectedFramework },
            ]
        }
        activeTab={activeTab}
        onTabChange={(id) => {
          if (id === "frameworks") {
            setControls([]);
            setControlsLoading(false);
            setControlsSearchTerm("");
            setSelectedFramework(null);
            if (searchInputRef.current) searchInputRef.current.value = "";
            setControlsPagination({ currentPage: 1, pageSize: 20, totalCount: 0, totalPages: 0 });
          }
          setActiveTab(id);
        }}
      />

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === "dashboard" && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ComplianceOverview
              customerId={
                user?.role === "customer_admin"
                  ? user?.id
                  : (user?.role === "security_analyst" || user?.role === "auditor")
                    ? selectedCompany?.user_id
                    : null
              }
              customerMasterId={customerMasterId}
            />
          </motion.div>
        )}

        {activeView === "interactive" && activeTab === "frameworks" && (
          <motion.div
            key="frameworks"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <InteractiveDashboardView />
          </motion.div>
        )}

        {activeView === "interactive" && activeTab === "controls" && (
          <motion.div
            key="controls"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ControlsTab
              selectedFramework={selectedFramework}
              controls={filteredControls}
              controlFormData={controlFormData}
              setControlFormData={setControlFormData}
              controlsLoading={controlsLoading}
              controlsPagination={controlsPagination}
              controlsSearchTerm={controlsSearchTerm}
              searchInputRef={searchInputRef}
              getStatusColor={getStatusColor}
              loadControls={loadControls}
              getFrameworkId={getFrameworkId}
              handleControlsSearch={handleControlsSearch}
              setControlsSearchTerm={setControlsSearchTerm}
              handleControlsPageChange={handleControlsPageChange}
              setShowControlModal={setShowControlModal}
              setShowEvidenceModal={setShowEvidenceModal}
              setShowControlDetailModal={setShowControlDetailModal}
              setSelectedControl={setSelectedControl}
              setSelectedControlForAssign={setSelectedControlForAssign}
              selectedControlForAssign={selectedControlForAssign}
              setShowAssignModal={setShowAssignModal}
              companyEmployees={companyEmployees}
              fetchCompanyEmployees={fetchCompanyEmployees}
              showAssignModal={showAssignModal}
              setAssignForm={setAssignForm}
              employeeSearch={employeeSearch}
              setEmployeeSearch={setEmployeeSearch}
              employeeDropdown={employeeDropdown}
              setEmployeeDropdown={setEmployeeDropdown}
              assignForm={assignForm}
              controlsFilters={controlsFilters}
              setControlsFilters={setControlsFilters}
              assignedControlsLoading={assignedControlsLoading}

            />
          </motion.div>
        )}

        {activeTab === "framework-master" && (
          <motion.div
            key="framework-master"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <FrameworkMasterManager
              sourceType={isSuperAdmin ? "framework-master" : "customer-framework"}
              customerId={managerCustomerId}
              canManageFrameworks={isSuperAdmin}
              canManageControls={isSuperAdmin}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Framework Modal */}
      
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-semibold mb-4">Assign Control</h2>

            {/* Employee Name */}
            {/* Employee Name */}
            <div className="mb-4 relative">
              <label className="block text-sm font-medium mb-1">Employee Name</label>

              <input
                type="text"
                value={assignForm.employee_name}
                onChange={(e) => {
                  const val = e.target.value;
                  setAssignForm(prev => ({ ...prev, employee_name: val })); // keep UI value
                  if (typeof setEmployeeSearch === "function") {
                    setEmployeeSearch(val); // triggers the debounced search useEffect in parent
                  } else {
                    console.warn("setEmployeeSearch is not available");
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Type employee name..."
              />

              {/* Dropdown rendered from employeeDropdown */}
              {employeeDropdown.length > 0 && (
                <div className="absolute z-50 bg-white border border-gray-300 rounded-md mt-1 w-full max-h-48 overflow-y-auto shadow-lg">
                  {employeeDropdown.map((emp) => (
                    <div
                      key={emp.employee_id}
                      onClick={() => {
                        setAssignForm(prev => ({
                          ...prev,
                          employee_id: emp.employee_id,
                          employee_name: `${emp.firstname} ${emp.lastname}`,
                        }));
                        setEmployeeDropdown([]);     // close dropdown
                      }}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {emp.firstname} {emp.lastname}
                    </div>
                  ))}
                </div>
              )}
            </div>



            {/* Due Date */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Due Date</label>
              <input
                type="date"
                value={assignForm.due_date}
                onChange={(e) =>
                  setAssignForm((prev) => ({ ...prev, due_date: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowAssignModal(false);
                  setAssignForm({ employee_name: "", due_date: "" });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleAssignSubmit}
                className="px-4 py-2 text-white rounded-md hover:opacity-90"
                style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Control Modal */}
      <AnimatePresence>
        {showControlModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
                initial={false}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
              className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Create New Control
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowControlModal(false);
                      resetControlForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Control ID *
                      </label>
                      <input
                        type="text"
                        value={controlFormData.control_id}
                        onChange={(e) =>
                          setControlFormData((prev) => ({
                            ...prev,
                            control_id: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., A.5.1, CC6.1"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Requirement Type
                      </label>
                      <select
                        value={controlFormData.requirement_type}
                        onChange={(e) =>
                          setControlFormData((prev) => ({
                            ...prev,
                            requirement_type: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="mandatory">Mandatory</option>
                        <option value="optional">Optional</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Control Title *
                    </label>
                    <input
                      type="text"
                      value={controlFormData.title}
                      onChange={(e) =>
                        setControlFormData((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Control title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      rows={4}
                      value={controlFormData.description}
                      onChange={(e) =>
                        setControlFormData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Describe the control requirement..."
                      required
                    />
                  </div>

                  {user.role !== "admin" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Assigned To
                        </label>
                        <input
                          type="email"
                          value={controlFormData.assigned_to}
                          onChange={(e) =>
                            setControlFormData((prev) => ({
                              ...prev,
                              assigned_to: e.target.value,
                            }))
                          }
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="assignee@company.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Due Date
                        </label>
                        <input
                          type="date"
                          value={controlFormData.due_date}
                          onChange={(e) =>
                            setControlFormData((prev) => ({
                              ...prev,
                              due_date: e.target.value,
                            }))
                          }
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>


                <div className="flex justify-end space-x-3 mt-8">
                  <button
                    type="button"
                    onClick={() => {
                      setShowControlModal(false);
                      resetControlForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateControl}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white hover:opacity-90"
                    style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
                  >
                    Create Control
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Control Detail Modal */}
      <AnimatePresence>
        {showControlDetailModal && selectedControl && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Control Details: {selectedControl.control_id}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowControlDetailModal(false);

                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Control Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 mb-3">
                      {selectedControl.title}
                    </h4>
                    <p className="text-sm text-gray-700 mb-3">
                      {selectedControl.description}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </label>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(
                            selectedControl.status
                          )}`}
                        >
                          {selectedControl.status}
                        </span>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </label>
                        <p className="text-gray-900">
                          {selectedControl.requirement_type}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned To
                        </label>
                        <p className="text-gray-900">
                          {selectedControl.assigned_to || "Unassigned"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Evidence
                        </label>
                        <p className="text-gray-900">
                          {selectedControl.evidence_count || 0} files
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Update Control Status */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 mb-3">
                      Update the Control
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Compliance Status
                        </label>
                        <select
                          disabled={editAccess}
                          value={controlUpdateData.status}
                          onChange={(e) =>
                            setControlUpdateData((prev) => ({
                              ...prev,
                              status: e.target.value,
                            }))
                          }
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="Yes">Yes - Compliant</option>
                          <option value="No">No - Not Compliant</option>
                          <option value="Partial">
                            Partial - In Progress
                          </option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Submission Status
                        </label>
                        <select
                          value={controlUpdateData.submission_status}
                          disabled={editAccess}
                          onChange={(e) =>
                            setControlUpdateData((prev) => ({
                              ...prev,
                              submission_status: e.target.value,
                            }))
                          }
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="Draft">Draft</option>
                          <option value="Submitted">Submitted</option>
                          <option value="Approved">Approved</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Evidence Description
                      </label> {editAccess ? (<p className="text-sm ml-4 mt-4 text-gray-400">{controlUpdateData.evidence_description || "Description not provided"}</p>) : (
                        <textarea
                          disabled={editAccess}
                          rows={3}
                          value={controlUpdateData.evidence_description}
                          onChange={(e) =>
                            setControlUpdateData((prev) => ({
                              ...prev,
                              evidence_description: e.target.value,
                            }))
                          }
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Describe the evidence or implementation..."
                        />)}
                    </div>


                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Remarks
                      </label>
                      {editAccess ? (
                        <p className="text-sm ml-4 mt-4 text-gray-400">{evidenceRemarks || "Remarks not provided"}</p>
                      ) : (
                        <textarea
                          rows={3}
                          value={evidenceRemarks}
                          onChange={(e) => setEvidenceRemarks(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Add remarks for this evidence..."
                        />
                      )}
                    </div>

                    {controlUpdateData.evidence_type !== "customize" && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Evidence Link
                        </label>
                        {getEvidenceLinkDetails(
                          selectedControl,
                          controlUpdateData.evidence_link,
                          controlUpdateData.evidence_category
                        ).length > 0 ? (
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50">
                            <div className="space-y-1">
                              {getEvidenceLinkDetails(
                                selectedControl,
                                controlUpdateData.evidence_link,
                                controlUpdateData.evidence_category
                              ).map((item, index) => (
                                <button
                                  key={`${item.url}-${index}`}
                                  type="button"
                                  onClick={() => setDocViewerItem(item)}
                                  className="flex items-center gap-1.5 w-full text-left underline break-all text-sm py-0.5 hover:opacity-80"
                                  style={{ color: "var(--app-primary-color, #26256b)" }}
                                >
                                  <LinkIcon className="w-3.5 h-3.5 shrink-0" />
                                  {`${index + 1}. Evidence${index + 1}${
                                    item.category ? ` (${item.category})` : ""
                                  }`}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value=""
                            readOnly
                            className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-500"
                            placeholder="Link not provided"
                          />
                        )}
                      </div>
                    )}
                    
                    <div className="mt-4 flex items-center justify-between">
                                          {!editAccess && (
                                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                              <input
                                                type="checkbox"
                                                checked={controlUpdateData.evidence_type === "customize"}
                                                onChange={(e) =>
                                                  setControlUpdateData((prev) => ({
                                                    ...prev,
                                                    evidence_type: e.target.checked ? "customize" : "default",
                                                  }))
                                                }
                                                className="h-4 w-4 border-gray-300 rounded focus:ring-2"
                                                style={{ accentColor: "var(--app-primary-color, #26256b)" }}
                                              />
                                              <span>Customize</span>
                                            </label>
                                          )}

                      {/* Download button (RIGHT) */}
                      {/* {selectedControl?.response_id ? (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              if (!selectedControl?.response_id) {
                                toast.error("No file available");
                                return;
                              }

                              const data = await downloadControlFileApi(
                                selectedControl.response_id
                              );

                              if (data?.download_url) {
                                window.open(data.download_url, "_blank");
                              } else {
                                toast.error("Download URL not found");
                              }
                            } catch (error) {
                              console.error("Download failed", error);
                              toast.error("Download failed");
                            }
                          }}
                          className="flex items-center gap-2 hover:opacity-80"
                          style={{ color: "var(--app-primary-color, #26256b)" }}
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                          <span>Download file</span>
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400">
                          No file
                        </span>
                      )} */}
                    </div>

                    {controlUpdateData.evidence_type === "customize" && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Evidence Type
                        </label>
                        {editAccess ? (
                          <p className="text-sm mt-4 text-gray-400">
                            {controlUpdateData.evidence_category || "Not selected"}
                          </p>
                        ) : (
                          <select
                            value={controlUpdateData.evidence_category}
                            onChange={(e) =>
                              setControlUpdateData((prev) => ({
                                ...prev,
                                evidence_category: e.target.value,
                              }))
                            }
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select evidence type</option>
                            <option value="policy">Policy</option>
                            <option value="document">Document</option>
                            <option value="process">Process</option>
                            <option value="procedure">Procedure</option>
                          </select>
                        )}

                        {!editAccess && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowEvidenceModal(true);
                            }}
                            className="mt-3 flex items-center gap-2 text-green-600 hover:text-green-900"
                          >
                            <CloudArrowUpIcon className="w-4 h-4" />
                            <span>Upload the file</span>
                          </button>
                        )}
                      </div>
                    )}

                    {!editAccess && (
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() =>
                            handleUpdateControl(
                              selectedControl.id,
                              controlUpdateData
                            )
                          }
                          className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white hover:opacity-90"
                          style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
                        >
                          Update Control
                        </button>
                      </div>)}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )
        }
      </AnimatePresence >

      <UploadFileModal
        showUploadFile={showEvidenceModal}
        setShowUploadFile={setShowEvidenceModal}
        selectedFile={uploadModalFile}
        setSelectedFile={setUploadModalFile}
        title="Upload Evidence"
        subtitle="Attach evidence file for this control"
        acceptedFileTypes=".pdf,.doc,.docx,.xlsx,.png,.jpg,.jpeg"
        fileTypeLabel={["PDF", "DOC", "DOCX", "XLSX", "PNG", "JPG", "JPEG"]}
        handleUploadFile={async () => {
          if (!uploadModalFile) {
            toast.error("Please upload a file");
            return;
          }
          setEvidenceFile(uploadModalFile);
          toast.success("File attached. Ready to update control.");
        }}
      />

      {/* Confirmation Modal */}
      < AnimatePresence >
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-md w-full"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {confirmModalData.title}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowConfirmModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  {confirmModalData.message}
                </p>

                <div className="flex justify-end space-x-3">
                  
                  <button
                    type="button"
                    onClick={() => setShowConfirmModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmModalData.onConfirm}
                    className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${confirmModalData.confirmButtonClass}`}
                    style={confirmModalData.confirmButtonStyle}
                  >
                    {confirmModalData.confirmText}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence >


{docViewerItem && (
  <DocViewerModal
    item={docViewerItem}
    employeeId={selectedControl?.employee_id || null}
    onClose={() => setDocViewerItem(null)}
  />
)}

<FrameworkModal
  show={showFrameworkModal}
  setShowFrameworkModal={setShowFrameworkModal}
  resetFrameworkForm={resetFrameworkForm}
  frameworkFormData={frameworkFormData}
  setFrameworkFormData={setFrameworkFormData}
  frameworkTypes={frameworkTypes}
  categories={categories}
  countries={countries}
  handleCreateFramework={handleCreateFramework}
/>
    {/* <ComplianceGapChatbot
      frameworkId={selectedFramework ? getFrameworkId(selectedFramework) : null}
      frameworkName={selectedFramework?.name || null}
    /> */}
    </div >
  );
};

export default ComplianceDashboard;